/* eslint-disable react-refresh/only-export-components */
// TODO(WIT-4362): fix
import styled, { css } from 'styled-components';
import { Select, MenuItem } from '@mui/material';
import type { SelectChangeEvent, SelectProps } from '@mui/material';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import { focusVisibleStylesNoWrapper, standardTransitionStyles } from 'design-system/src/sharedStyles';
import React from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';

export type DropdownChangeEvent = SelectChangeEvent<unknown>;
export const DropdownItem = MenuItem;

export type DropdownSize = 'sm' | 'lg';

export const dropdownPadding = css`
  padding: 10px 12px;
`;

export const dropdownSmallPadding = css`
  padding: 6px 12px;
`;

// Mimics bodyDMSans typography style
export const dropdownTextStyling = css`
  font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
`;

// Mimics bodySmallDMSans typography style
export const dropdownSmallTextStyling = css`
  font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
`;

export const dropdownFieldsetStyling = css`
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--grayscale-100) !important;
  ${standardTransitionStyles}
`;

export const dropdownDisabledStyling = css`
  background-color: var(--grayscale-50);

  * {
    cursor: not-allowed !important;
  }

  > svg {
    color: var(--grayscale-100);
  }
`;

export const dropdownErrorStyling = css`
  > fieldset {
    border: 1px solid var(--red-400) !important;
  }
`;

export const dropdownSvgStyling = css`
  color: var(--grayscale-900);
  right: 12px;
  transition: transform 0.2s ease-out;
  top: auto;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// TODO: style text
// TODO: style overlay
export const Dropdown = styled(Select).attrs({
  IconComponent: ChevronDown,
})<{ $hasError?: boolean; $size?: DropdownSize }>`
  // if the height isn't set, the height will shift by 1px when an item is selected
  height: ${({ $size }) => ($size === 'sm' ? '32px' : '44px')};

  > [role='combobox'] {
    ${({ $size }) => ($size === 'sm' ? dropdownSmallPadding : dropdownPadding)}

    // TODO(typography): use the typography styles after they're exported
    ${({ $size }) => ($size === 'sm' ? dropdownSmallTextStyling : dropdownTextStyling)}
  }

  > fieldset {
    ${dropdownFieldsetStyling}
  }

  // stops the empty legend from breaking the focus outline of the input field
  > fieldset > legend {
    width: 0px;
  }

  > svg {
    ${dropdownSvgStyling}
  }

  &.Mui-focused > fieldset {
    border: 1px solid var(--grayscale-100) !important;
    ${focusVisibleStylesNoWrapper}
  }

  &.Mui-disabled {
    ${dropdownDisabledStyling}
  }

  ${({ $hasError }) => $hasError && dropdownErrorStyling}
`;

const StyledPlaceholder = styled(Typography)`
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 100%;
`;

/**
 * Enhanced Dropdown component with placeholder support
 * TODO: Merge this with DropdownWithPlaceholder in patient app
 */
export const DropdownWithPlaceholder = React.forwardRef<HTMLDivElement, SelectProps & { $hasError?: boolean }>(
  ({ placeholder = 'Select an option', value, children, $hasError, ...rest }, ref) => {
    return (
      <Dropdown ref={ref} value={value ?? ''} $hasError={$hasError} displayEmpty {...rest}>
        {placeholder && (
          // Inline style needed to hide the item from the dropdown list
          <DropdownItem value="" disabled sx={{ display: 'none' }}>
            <StyledPlaceholder color="var(--grayscale-300)">{placeholder}</StyledPlaceholder>
          </DropdownItem>
        )}
        {children}
      </Dropdown>
    );
  },
);

// TODO: make dropdown component that can accept a label and helper text
// and control the spacing between them
export const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
